import Cookie from 'js-cookie';

import type { GlsInitResponse } from '@/api/gls/types';
import { ANDROID_WRAPPER_EVENTS } from '@/event-manager/types';
import { PLATFORMS } from '../constants/game';
import { getPlatform } from '../getters/gameContextGetters';

const WRAPPER_COOKIE = 'wrapper';

export const isIosWrapper = (): boolean => {
  try {
    return (
      Cookie.get(WRAPPER_COOKIE) === 'iOS' ||
      window?.MobileWrapper?.getWrapperType?.() === 'iOS'
    );
  } catch {
    return false;
  }
};

/**
 * True when running inside any native wrapper webview (iOS MobileWrapper or
 * AndroidWrapper). Used to gate wrapper-only behaviour (e.g. honouring
 * REQUEST_GAME_CLOSE).
 */
export const isRunningInsideWrapper = (): boolean => {
  try {
    return !!(
      window?.MobileWrapper?.isWrapper?.() ||
      window?.AndroidWrapper?.isWrapper?.()
    );
  } catch {
    return false;
  }
};

export const isAndroidWrapper = () => {
  try {
    return (
      (window.AndroidWrapper !== undefined &&
        window.AndroidWrapper.isWrapper()) ||
      window?.MobileWrapper?.getWrapperType?.() === PLATFORMS.Android
    );
  } catch {
    return false;
  }
};

export const sendAndroidWrapperEvent = (eventType: string, data?: unknown) => {
  window?.MobileWrapper?.onEvent?.(eventType, data);
  window?.AndroidWrapper?.onEvent?.(eventType, data);
};

export const isNativeGlsFetchSupported = (): boolean => {
  try {
    return (
      isAndroidWrapper() &&
      typeof window?.AndroidWrapper?.nativeGlsExperiment === 'function' &&
      window.AndroidWrapper.nativeGlsExperiment() &&
      typeof window?.AndroidWrapper?.getGameData === 'function'
    );
  } catch {
    return false;
  }
};

/** True when the native app reports the native GLS experiment is enabled. */
export const isNativeGlsExperimentEnabled = (): boolean => {
  try {
    return window?.AndroidWrapper?.nativeGlsExperiment?.() === true;
  } catch {
    return false;
  }
};

/** Native returns an empty string while its GLS fetch is still in flight. */
export const getGameDataFromNative = (): GlsInitResponse | undefined => {
  const response = window?.AndroidWrapper?.getGameData?.();

  return response?.trim()
    ? (JSON.parse(response) as GlsInitResponse)
    : undefined;
};

/**
 * Check if wrapped inside Native app (Android | AndroidXsell)
 * @returns isAndroid AndroidWrapper
 * @returns isXsell XsellWrapper
 * @returns isMobile AndroidWrapper or XsellWrapper
 */
export const checkWrapperType = () => {
  const isAndroid = isAndroidWrapper();
  const isXsell = getPlatform() === PLATFORMS.XSELL;
  const isMobile = isAndroid || isXsell;
  return { isAndroid, isXsell, isMobile };
};

export const checkTypeOfWrapper = () => checkWrapperType();

export const sendXsellDepositMessage = () => {
  if (getPlatform() === PLATFORMS.XSELL && isAndroidWrapper()) {
    sendAndroidWrapperEvent(ANDROID_WRAPPER_EVENTS.SHOW_DEPOSIT, '');
  }
};

export const triggerXsellLogin = () => {
  if (getPlatform() === PLATFORMS.XSELL && isAndroidWrapper()) {
    window.AndroidSportsbook?.login?.();
  }
};
